import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  ArrowLeft, Coins, Users, Gift, Broadcast, Lightning, ChartLineUp,
  Wallet as WalletIcon, Crown, Sparkle, Trophy, ArrowRight,
} from "@phosphor-icons/react";

export default function CreatorCenter() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/creator/center")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Couldn't load your Creator Center."));
  }, []);

  if (err) {
    return (
      <div className="min-h-[100dvh] bg-ink flex flex-col items-center justify-center gap-4 text-cream/60 px-6 text-center" data-testid="creator-center-error">
        <p>{err}</p>
        <button onClick={() => nav("/profile")} className="px-5 py-2.5 rounded-full bg-papaya text-ink text-xs font-black tracking-[0.2em] uppercase">Back to profile</button>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-[100dvh] flex items-center justify-center text-cream/40 bg-ink">Loading your Creator Center…</div>;
  }

  const e = data.earnings || {};
  const a = data.audience || {};
  const g = data.gifts || {};
  const live = data.live || {};
  const promo = data.promotions || {};
  const badge = data.badge || {};

  const actions = [
    { to: "/analytics", icon: ChartLineUp, label: "Analytics", tint: "text-papaya" },
    { to: "/earnings", icon: Coins, label: "Earnings & payout", tint: "text-pink-300" },
    { to: "/wallet", icon: WalletIcon, label: "Wallet", tint: "text-papaya" },
    { to: "/gifts", icon: Gift, label: "Gift Shop", tint: "text-amber-300" },
    { to: "/hub", icon: Sparkle, label: "AI Studio", tint: "text-violet-300" },
    { to: "/leaderboard", icon: Trophy, label: "Leaderboard", tint: "text-amber-300" },
  ];

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="creator-center-page">
      <header className="sticky top-0 z-30 crystal-glass px-5 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => nav(-1)} data-testid="creator-center-back" className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Creator</p>
          <h1 className="text-cream text-2xl font-black tracking-tighter">Creator Center</h1>
        </div>
        {badge.emoji && (
          <span data-testid="creator-center-badge" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-papaya/15 border border-papaya/40 text-papaya text-xs font-black">
            <span>{badge.emoji}</span>{badge.label}
          </span>
        )}
      </header>

      <div className="px-5 mt-4 space-y-5">
        {/* Earnings hero */}
        <Link to="/earnings" data-testid="creator-center-earnings" className="block relative overflow-hidden rounded-3xl border border-papaya/40 p-5 hover:border-papaya/70 transition-colors">
          <div className="absolute inset-0 bg-gradient-to-br from-papaya/25 via-ink to-pink-500/10" />
          <div className="relative">
            <p className="text-cream/70 text-[10px] tracking-[0.3em] uppercase font-bold">Available to withdraw</p>
            <div className="flex items-end gap-2 mt-1">
              <span className="text-cream text-4xl font-black tracking-tighter">{(e.pearls_balance || 0).toLocaleString()}</span>
              <span className="text-cream/60 text-sm font-bold mb-1">Pearls · ${e.usd_estimate ?? 0}</span>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs">
              <span className="text-cream/70">Pending <b className="text-cream">{(e.pending_pearls || 0).toLocaleString()}</b> · ${e.pending_usd ?? 0}</span>
              <span className="text-cream/70">Lifetime <b className="text-cream">{(e.pearls_lifetime || 0).toLocaleString()}</b></span>
            </div>
            <p className="text-cream/50 text-[11px] mt-2">Payout minimum {(e.payout_min_pearls || 0).toLocaleString()} Pearls (${e.payout_min_usd ?? 0})</p>
            <span className="inline-flex items-center gap-1 mt-3 text-papaya text-[11px] font-black tracking-[0.15em] uppercase">Request payout <ArrowRight size={12} weight="bold" /></span>
          </div>
        </Link>

        {/* Stat grid */}
        <div className="grid grid-cols-2 gap-3" data-testid="creator-center-stats">
          <Stat icon={Users} tint="text-papaya" value={a.followers} label="Followers" />
          <Stat icon={Crown} tint="text-pink-300" value={a.subscribers} label="Subscribers" />
          <Stat icon={ChartLineUp} tint="text-papaya" value={a.total_views} label="Total views" />
          <Stat icon={Lightning} tint="text-amber-300" value={a.total_likes} label="Total likes" />
          <Stat icon={Gift} tint="text-amber-300" value={g.count} label="Gifts received" sub={`${(g.pearls || 0).toLocaleString()} Pearls`} />
          <Stat icon={Broadcast} tint="text-red-300" value={live.sessions} label="LIVE sessions" sub={live.league?.label ? `${live.league.label} · ${(live.league_rewards_waves || 0).toLocaleString()} 🪙` : undefined} />
        </div>

        {/* Promotions */}
        <div className="rounded-3xl bg-white/5 border border-white/5 p-4" data-testid="creator-center-promotions">
          <div className="flex items-center gap-2">
            <Lightning size={18} weight="fill" className="text-papaya" />
            <p className="text-cream font-black tracking-tight">Promotions</p>
          </div>
          <p className="text-cream/60 text-sm mt-1">
            {promo.active_boosts > 0
              ? `${promo.active_boosts} active post boost${promo.active_boosts > 1 ? "s" : ""} running now.`
              : "No active boosts. Boost a post to reach more of the wave."}
          </p>
        </div>

        {/* Quick actions */}
        <div>
          <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3">Manage</p>
          <div className="grid grid-cols-3 gap-2" data-testid="creator-center-actions">
            {actions.map((ac) => {
              const I = ac.icon;
              return (
                <Link key={ac.to} to={ac.to} data-testid={`creator-center-action-${ac.label.split(" ")[0].toLowerCase()}`} className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-white/5 border border-white/5 hover:border-papaya/40 transition-colors text-cream">
                  <I size={22} weight="duotone" className={ac.tint} />
                  <span className="text-[10px] tracking-[0.1em] uppercase font-bold text-center leading-tight">{ac.label}</span>
                </Link>
              );
            })}
          </div>
        </div>

        <Link to="/backpack" data-testid="creator-center-to-backpack" className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-papaya/40 transition-colors">
          <Trophy size={22} weight="duotone" className="text-amber-300" />
          <div className="flex-1">
            <p className="text-cream font-bold">Rewards Backpack</p>
            <p className="text-cream/50 text-xs">Gifts, titles, badges & league rewards you've earned</p>
          </div>
          <ArrowRight size={16} className="text-cream/40" />
        </Link>
      </div>
    </div>
  );
}

function Stat({ icon: I, tint, value, label, sub }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/5 p-4">
      <I size={20} weight="duotone" className={tint} />
      <p className="text-cream font-black text-2xl tracking-tighter mt-2">{(value || 0).toLocaleString()}</p>
      <p className="text-cream/50 text-[10px] tracking-[0.15em] uppercase font-bold">{label}</p>
      {sub && <p className="text-cream/40 text-[10px] mt-0.5">{sub}</p>}
    </div>
  );
}
